//pages/index.jsx

import { useState } from 'react';
import { getPageSEO } from '../utils/helpers/seo';
// Critical above-the-fold components (loaded immediately)
import { FooterSection, HeaderSection, SEOWrapper } from '../components/layout';
import { HeroSection, GeneralSection, FleetSection, ServicesSection, TestimonialsSection, HowItWorksSection, FeaturesSection, CTASection, TrustedBySection, FAQSection, LocationsSection } from '../components/sections';
import { usePageData } from '../utils/hooks';

import { Loader } from '../components/common';
import { fetchConfigPage, fetchHomePage } from '../utils/api/api';

export default function Home({ initialData, error }) {
    const [strapiData, setStrapiData] = useState(initialData);
    const [apiError] = useState(error);

    // Extract all data using custom hook
    const {
        configData,
        pageData,
        header,
        footer,
        heroSection,
        generalContent,
        fleetData,
        trustedByData,
        servicesData,
        testimonialsData,
        howItWorksData,
        featuresData,
        ctaData,
        locationsData
    } = usePageData(strapiData);

    // Get FAQ data with special structure for home page
    const faqData = {
        order: pageData.Faqs?.order || pageData.FAQ?.order || pageData.faqs?.order || 12,
        title: pageData.Faqs?.title || pageData.FAQ?.title || pageData.faqs?.title || 'FAQs',
        description: pageData.Faqs?.description || pageData.FAQ?.description || pageData.faqs?.description || '',
        faqs: pageData.Faqs?.faqs || pageData.FAQ?.faqs || pageData.faqs?.faqs || [],
    };

    // Create sections array with order - handle nested arrays
    const createOrderedSections = () => {
        const allSections = [];
        
        // Process GeneralSection - extract individual items
        if (generalContent) {
            const generalItems = Array.isArray(generalContent) ? generalContent : [generalContent];
            generalItems.forEach((item, index) => {
                allSections.push({
                    component: <GeneralSection data={[item]} globalIndex={index} />,
                    order: item.order || item.Order || 1 + (index * 0.1) // Add small offset for same order
                });
            });
        }
        
        // Add other sections
        if (trustedByData) {
            allSections.push({
                component: <TrustedBySection data={trustedByData} />,
                order: trustedByData.order || trustedByData.Order || 2
            });
        }
        
        if (featuresData) {
            allSections.push({
                component: <FeaturesSection data={featuresData} />,
                order: featuresData.order || featuresData.Order || 3
            });
        }
        
        if (fleetData) {
            allSections.push({
                component: <FleetSection fleetData={fleetData} />,
                order: fleetData.order || fleetData.Order || 4
            });
        }
        
        if (locationsData) {
            allSections.push({
                component: <LocationsSection data={locationsData} />,
                order: locationsData.order || locationsData.Order || 4.5
            });
        }
        
        if (servicesData) {
            allSections.push({
                component: <ServicesSection data={servicesData} />,
                order: servicesData.order || servicesData.Order || 5
            });
        }
        
        if (howItWorksData) {
            allSections.push({
                component: <HowItWorksSection data={howItWorksData} />,
                order: howItWorksData.order || howItWorksData.Order || 6
            });
        }
        
        if (testimonialsData) {
            allSections.push({
                component: <TestimonialsSection data={testimonialsData} />,
                order: testimonialsData.order || testimonialsData.Order || 7
            });
        }
        
        if (faqData) {
            allSections.push({
                component: <FAQSection data={faqData} />,
                order: faqData.order || faqData.Order || 12
            });
        }
        
        if (ctaData) {
            allSections.push({
                component: <CTASection data={ctaData} />,
                order: ctaData.order || ctaData.Order || 13
            });
        }
        
        // Sort by order
        return allSections.sort((a, b) => a.order - b.order);
    };

    const sections = createOrderedSections();
    
    // Priority 1: Show error if there's an error
    if (apiError) {
        return <Loader type="error" message="Oops! Something Went Wrong" description="Please wait a moment and try again. We'll be back up shortly!" fullPage={true} action={() => window.location.reload()} actionText="Refresh Page" />;
    }

    // Priority 2: Show loading if no data yet (shouldn't happen with SSR but handles client-side navigation)
    if (!initialData) {
        return <Loader type="loading" message="Loading page..." description="Please wait while we prepare your experience." fullPage={true} />;
    }

    // Priority 3: Show empty state if data exists but is invalid/empty
    if (!pageData || Object.keys(pageData).length === 0) {
        return <Loader type="empty" message="No Content Available" description="This page doesn't have any content configured yet. Please check back later." fullPage={true} action={() => window.location.reload()} actionText="Refresh Page" />;
    }

    // Generate SEO data for home page
    const seoData = getPageSEO(pageData, configData, strapiData.pageUrl);


    return (
        <>
            <SEOWrapper 
                pageData={pageData}
                configData={configData}
                pageUrl={strapiData.pageUrl}
                seoData={seoData}
            />

            {/* Critical above-the-fold components load immediately */}
            <HeaderSection data={header} pageUrl={strapiData.pageUrl} phoneNumber={pageData.EnhancedHeroSection?.phone} />
            <HeroSection data={heroSection} phoneNumber={header?.Contact} />

            <section style={{ display: 'flex', flexDirection: 'column' }}>
                {/* All sections rendered in order */}
                {sections.map((section, index) => (
                    <div key={index} style={{ order: section.order }}>
                        {section.component}
                    </div>
                ))}
            </section>

            {/* Footer Section - lazy loaded */}
            <FooterSection data={footer} />
        </>
    );
}

export async function getServerSideProps({ req, res }) {
    // Add caching headers for better performance
    res.setHeader('Cache-Control', 'public, s-maxage=300, stale-while-revalidate=600');
    
    try {
        // Parallelize API calls to reduce server response time
        const [pageData, configData] = await Promise.all([
            fetchHomePage(),
            fetchConfigPage()
        ]);

        // Check if data is invalid/empty
        if (
            !pageData?.data || 
            typeof pageData.data !== 'object' || 
            (Array.isArray(pageData.data) && pageData.data.every(item => item == null))
        ) {
            // Return empty data (not null) so component can show empty state
            return {
                props: {
                    initialData: {
                        configData: configData || {},
                        pageData: {},
                        pageUrl: '/',
                    },
                    error: null,
                },
            };
        }

        // Return valid data
        return {
            props: {
                initialData: {
                    configData: configData,
                    pageData: pageData.data,
                    pageUrl: '/',
                },
                error: null,
            },
        };
    } catch (err) {
        // Return error (with null initialData)
        return {
            props: {
                initialData: null,
                error: err.message || 'An unexpected error occurred while loading the page.',
            },
        };
    }
}

